import { useCallback, useState } from 'react';
import { useBusiness } from '../contexts/BusinessContext';
import { fetchWebsiteYear, type WebsiteReportMonth, type WebsiteReportType } from '../lib/websiteVisitsReport';

export function useWebsiteVisitsByMonthYtd() {
  const { selectedBusiness } = useBusiness();
  const businessId = selectedBusiness?.id;
  const timeZone = selectedBusiness?.timezone || 'America/Vancouver';
  const [months, setMonths] = useState<WebsiteReportMonth[]>([]);
  const [yearVisits, setYearVisits] = useState(0);
  const [yearUnique, setYearUnique] = useState(0);
  const [yearConversionRate, setYearConversionRate] = useState<number | undefined>();
  const [yearConversionTypes, setYearConversionTypes] = useState<WebsiteReportType[]>([]);
  const [yearLabel, setYearLabel] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!businessId) {
      setMonths([]);
      setYearVisits(0);
      setYearUnique(0);
      setYearConversionRate(undefined);
      setYearConversionTypes([]);
      setYearLabel('');
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const year = await fetchWebsiteYear(businessId, timeZone);
      setMonths(year.months);
      setYearVisits(year.visits);
      setYearUnique(year.unique);
      setYearConversionRate(year.conversionRate);
      setYearConversionTypes(year.conversionTypes);
      setYearLabel(year.yearLabel);
    } catch (err) {
      console.error('[useWebsiteVisitsByMonthYtd]', err);
      setMonths([]);
      setYearVisits(0);
      setYearUnique(0);
      setYearConversionRate(undefined);
      setYearConversionTypes([]);
      setError('Could not load website visits by month.');
    } finally {
      setLoading(false);
    }
  }, [businessId, timeZone]);

  return {
    months,
    yearVisits,
    yearUnique,
    yearConversionRate,
    yearConversionTypes,
    yearLabel,
    loading,
    error,
    load,
    businessId,
    timeZone,
  };
}
